import { useState, useEffect } from "react";
import { Map, MapboxGeoJSONFeature } from "mapbox-gl";
import { useTranslation } from "react-i18next";
import { LocationData, MapTypeData } from "../map.interfaces";
import { CmsMapData } from "./useCmsMapData";
import { mapboxCustomButton } from "../controls/mapboxCustomButton";
import { CustomMapboxControl } from "../controls/customMapboxControl";
import mapboxgl from "mapbox-gl";
import { MapTypes } from "../mapConfig";
import { forEach } from "lodash";
import { SearchData } from "./useMapData";
import _ from "lodash";

export interface MapEvents {
  map: Map | null;
  mapContainerRef: React.RefObject<HTMLDivElement>;
  cmsMapData: CmsMapData[] | null;
  currentCmsMapData: CmsMapData | null;
  customMapLayers: mapboxgl.AnyLayer[];
  mapConfig: MapTypeData | undefined;
  hideMapMenu: boolean;
  mapLinkedWithPage: boolean;
  searchData: SearchData[] | undefined;
  popupClosed: boolean;
}

export interface SelectedFeature {
  selected: boolean;
  feature: MapboxGeoJSONFeature;
}

export const useMapEvents = ({
  map,
  mapContainerRef,
  cmsMapData,
  currentCmsMapData,
  customMapLayers,
  mapConfig,
  hideMapMenu,
  mapLinkedWithPage,
  searchData,
  popupClosed,
}: MapEvents) => {
  const [selectedLocationData, setSelectedLocationData] = useState<
    LocationData | undefined
  >();
  const [showMapMenu, setShowMapMenu] = useState(false);
  const [mapSelectionCleared, setMapSelectionCleared] = useState(true);
  const [updateControls, setUpdateControls] = useState(false);
  const [mapMenuControl, setMapMenuControl] =
    useState<mapboxgl.IControl | null>(null);
  const [fullscreenControl, setFullscreenControl] =
    useState<mapboxgl.FullscreenControl | null>(null);
  const [navigationControl, setNavigationControl] =
    useState<mapboxgl.IControl | null>(null);
  const [goHomeControl, setGoHomeControl] = useState<mapboxgl.IControl | null>(
    null,
  );
  const [selectedMapboxFeatures, setSelectedMapboxFeatures] = useState<
    SelectedFeature[]
  >([]);
  const [hoveredItemName, setHoveredItemName] = useState("");

  const { t } = useTranslation();

  const layerNodes = [];

  // --------------------------------------------------------
  // HANDLE MAP INTERACTION - when a layer is clicked on
  // --------------------------------------------------------

  useEffect(() => {
    if (map && currentCmsMapData && customMapLayers && mapConfig) {
      if (mapMenuControl) {
        if (map.hasControl(mapMenuControl)) {
          map.removeControl(mapMenuControl);
        }
      }

      if (fullscreenControl) {
        if (map.hasControl(fullscreenControl)) {
          map.removeControl(fullscreenControl);
        }
      }

      if (navigationControl) {
        if (map.hasControl(navigationControl)) {
          map.removeControl(navigationControl);
        }
      }

      if (goHomeControl) {
        if (map.hasControl(goHomeControl)) {
          map.removeControl(goHomeControl);
        }
      }

      setMapMenuControl(null);
      setFullscreenControl(null);
      setNavigationControl(null);
      setGoHomeControl(null);
      setUpdateControls(true);

      const cleanupHandlers = customMapLayers.map((layer) => {
        const cleanup = handleMapLayerIntraction(layer);
        setMapFeaturePaintSettings(layer);
        return cleanup;
      });

      return () => {
        cleanupHandlers.forEach((cleanup) => cleanup?.());
      };
    }
  }, [map, mapConfig, currentCmsMapData, customMapLayers, searchData]);

  useEffect(() => {
    setShowMapMenu(false);
  }, [hideMapMenu]);

  useEffect(() => {
    if (map && popupClosed && !mapSelectionCleared) {
      selectedMapboxFeatures.forEach((feature, index) => {
        feature.selected = false;
      });
      setSelectedMapboxFeatures([...selectedMapboxFeatures]);

      setMapSelectionCleared(true);
    }
  }, [popupClosed]);

  useEffect(() => {
    if (map && mapContainerRef && cmsMapData && updateControls) {
      const disableMapChangeButton =
        currentCmsMapData?.disableMapChange && mapLinkedWithPage;
      if (!disableMapChangeButton) {
        if (mapMenuControl) {
          map.removeControl(mapMenuControl);
          map.addControl(mapMenuControl);
        } else {
          setMapMenuControl(
            new CustomMapboxControl({
              htmlString: mapboxCustomButton({
                title: t("map.changeMap"),
                icon: "bi bi-three-dots-vertical",
              }),
              onCallback: () => setShowMapMenu((showMapMenu) => !showMapMenu),
            }),
          );
        }
      }

      if (fullscreenControl) {
        map.addControl(fullscreenControl);
      } else {
        setFullscreenControl(
          new mapboxgl.FullscreenControl({
            container: mapContainerRef.current,
          }),
        );
      }

      if (navigationControl) {
        map.addControl(navigationControl);
      } else {
        setNavigationControl(new mapboxgl.NavigationControl());
      }

      if (goHomeControl) {
        map.addControl(goHomeControl);
      } else {
        setGoHomeControl(
          new CustomMapboxControl({
            htmlString: mapboxCustomButton({
              title: t("map.goHome"),
              icon: "bi bi-house-fill",
            }),
            onCallback: () => navigateHome(),
          }),
        );
      }
    }
  }, [
    updateControls,
    mapMenuControl,
    fullscreenControl,
    navigationControl,
    goHomeControl,
  ]);

  // --------------------------------------------------------
  // NAVIGATE HOME - the user flyes to the origin coordinates
  // --------------------------------------------------------

  const navigateHome = () => {
    if (mapConfig && map) {
      map.flyTo({
        center: [mapConfig.longitude, mapConfig.latitude],
        pitch: mapConfig?.pitch,
        bearing: mapConfig?.bearing,
        zoom: mapConfig?.zoom,
        padding: 0,
      });
    }
  };

  const setMapFeaturePaintSettings = (mbLayer: mapboxgl.AnyLayer) => {
    if (map && currentCmsMapData) {
      if (currentCmsMapData.mapType === MapTypes.LINER_SERVICE_MAP) {
        const circleColorFilter = map.getPaintProperty(
          "ghab-linermap",
          "circle-color",
        );
        const circleRadiusFilter = map.getPaintProperty(
          "ghab-linermap",
          "circle-radius",
        );

        if (
          !Array.isArray(circleColorFilter) ||
          circleColorFilter.length < 4 ||
          !Array.isArray(circleRadiusFilter) ||
          circleRadiusFilter.length < 4
        ) {
          return;
        }

        map.setPaintProperty("ghab-linermap", "circle-color", [
          "case",
          [
            "all",
            ["==", ["feature-state", "nodeId"], ["get", "nodeId"]],
            ["==", ["feature-state", "selected"], true],
          ],
          circleColorFilter[2],
          circleColorFilter[3],
        ]);

        map.setPaintProperty("ghab-linermap", "circle-radius", [
          "case",
          [
            "all",
            ["==", ["feature-state", "nodeId"], ["get", "nodeId"]],
            ["==", ["feature-state", "selected"], true],
          ],
          circleRadiusFilter[2],
          circleRadiusFilter[3],
        ]);
      }
    }
  };

  // ------------------------------------------------------------
  // useEffect - selectedMapboxFeatures
  // ------------------------------------------------------------

  useEffect(() => {
    if (map) {
      selectedMapboxFeatures.forEach(({ selected, feature }, index) => {
        if (map.getFeatureState(feature)?.nodeId) {
          map.removeFeatureState(feature);
        }
      });

      selectedMapboxFeatures.forEach(({ selected, feature }, index) => {
        const selectedNodeId = (feature as any).properties.nodeId;
        if (selectedNodeId) {
          if (selected) {
            map.setFeatureState(feature, { selected, nodeId: selectedNodeId });
          }
        }
      });
    }
  }, [selectedMapboxFeatures]);

  // --------------------------------------------------------
  // HANDLE MAP INTERACTION - when a layer is clicked on
  // --------------------------------------------------------

  const handleMapLayerIntraction = (mbLayer: mapboxgl.AnyLayer) => {
    if (map && currentCmsMapData) {
      const onClick = (e: mapboxgl.MapLayerMouseEvent) => {
        const pageLayerSettings = currentCmsMapData.mapLayers.find(
          (layer) => layer.layerId.toString() === mbLayer.id.toString(),
        );

        if (pageLayerSettings) {
          if (e.features) {
            const propertiesData = e.features[0].properties;
            const nodeId = propertiesData && propertiesData["nodeId"];

            const propertiesForLayers = pageLayerSettings?.mapNodes?.find(
              (node) => node.nodeId.toString() === nodeId.toString(),
            );

            setMapSelectionCleared(false);
            setSelectedLocationData({
              lngLat: e.lngLat,
              features: e.features,
              propertyData: propertiesData,
              propertiesForLayer: propertiesForLayers,
            });
          }
        }

        //Linjekarta
        if (currentCmsMapData.mapType === MapTypes.LINER_SERVICE_MAP) {
          if (e.features) {
            selectedMapboxFeatures.forEach((selectedFeature, index) => {
              selectedFeature.selected = false;
            });
            const featureVisited = selectedMapboxFeatures.find(
              ({ feature }) =>
                e.features &&
                (e.features[0] as any).properties.nodeId ===
                  (feature as any).properties.nodeId,
            );
            if (featureVisited) {
              featureVisited.selected = true;
              setSelectedMapboxFeatures([...selectedMapboxFeatures]);
            } else {
              selectedMapboxFeatures.unshift({
                selected: true,
                feature: e.features[0],
              });
              setSelectedMapboxFeatures([...selectedMapboxFeatures]);
            }
            const clickedPropertiesData = e.features[0].properties;
            const matchedSearchData = searchData?.find(
              (item) =>
                String(item.mapboxFeatureData.properties?.nodeId) ===
                String(clickedPropertiesData?.nodeId),
            );
            const propertiesData = matchedSearchData?.mapboxFeatureData
              ?.properties
              ? {
                  ...matchedSearchData.mapboxFeatureData.properties,
                  routes: JSON.stringify(
                    matchedSearchData.mapboxFeatureData.properties.routes || [],
                  ),
                }
              : clickedPropertiesData;

            setMapSelectionCleared(false);
            setSelectedLocationData({
              lngLat: e.lngLat,
              features: e.features,
              propertyData: propertiesData,
            });
          }
        }
      };

      const onMouseMove = (e: mapboxgl.MapLayerMouseEvent) => {
        map.getCanvas().style.cursor = "pointer";
        if (e.features && e.features[0]) {
          const currentLang = document.documentElement.lang;
          const itemName =
            e.features[0].properties?.["name_" + currentLang] || "";
          setHoveredItemName(itemName);
        }
      };
      const onMouseLeave = () => {
        map.getCanvas().style.cursor = "";
        setHoveredItemName("");
      };

      map.on("click", mbLayer.id, onClick);
      map.on("mousemove", mbLayer.id, onMouseMove);
      map.on("mouseleave", mbLayer.id, onMouseLeave);

      return () => {
        map.off("click", mbLayer.id, onClick);
        map.off("mousemove", mbLayer.id, onMouseMove);
        map.off("mouseleave", mbLayer.id, onMouseLeave);
      };
    }

    return undefined;
  };

  return { selectedLocationData, showMapMenu, hoveredItemName };
};
